import { useEffect, useRef, useCallback } from 'react';
import { useAxiosClient } from './providers/ApiProvider';
import { useAuthState } from './providers/AuthProvider';
import networkService from './services/networkService';
import { processAppointments } from './prefetchSync';

export { prefetchForPatient, upsertPrefetchedSedation } from './prefetchSync';

// ─── Polling hook (used by AuthenticatedApp) ─────────────────────────────────
// ponytail: timer runs in a Web Worker per spec requirement ("not on the main thread")

export function useUpcomingPrefetch() {
  const axiosClient = useAxiosClient();
  const authState = useAuthState();
  const workerRef = useRef<Worker | null>(null);

  const runPrefetchAll = useCallback(async () => {
    if (!networkService.isOnline() || !axiosClient) return;
    try {
      const response = await axiosClient.schedule.getApiScheduleUpcomingPrefetch(72);
      const appointments = response?.data?.appointments ?? [];
      await processAppointments(appointments);
    } catch {
      // silent — offline/auth failures during background polling are expected
    }
  }, [axiosClient]);

  useEffect(() => {
    if (!authState.isAuthenticated || !axiosClient) return;

    const worker = new Worker(new URL('../workers/prefetchWorker.ts', import.meta.url));
    workerRef.current = worker;
    worker.onmessage = (e: MessageEvent) => {
      if (e.data?.type === 'PREFETCH_TICK') runPrefetchAll();
    };
    worker.postMessage({ type: 'START', intervalMs: 15 * 60 * 1000 });

    runPrefetchAll();

    return () => {
      worker.terminate();
      workerRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState.isAuthenticated, axiosClient]);
}
